<template>
  <div class="product-price-details">
    <table class="product-price-details__prices">
      <tr
          v-for="(row) in rowsPrices"
          :key="row.label"
      >
        <td class="fs-regular">
          <span v-html="row.label"></span><sup class="d-none d-print-inline">{{ row.sub }}</sup>&nbsp;
          <IoTooltip v-if="row.info" class="d-print-none">
            <template v-slot:title>
              {{ row.info }}
            </template>
            <FontAwesomeIcon :icon="faInfoCircle" />
          </IoTooltip>
        </td>
        <td
            class="fs-regular"
            v-html="row.value"
        ></td>
      </tr>
    </table>
    <div class="product-price-details__downloads">
      <a
          v-for="row in rowsDownloads"
          :key="`download--${row.key}`"
          :href="row.href"
          target="_blank"
      >
        <span>
            <FontAwesomeIcon :icon="faDownload" />
        </span>
        <span>
          {{ row.label }}
        </span>
      </a>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  ref,
  onMounted,
} from 'vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faDownload,
  faInfoCircle,
} from '@fortawesome/pro-regular-svg-icons';
import Product from '@/classes/Product';

library.add(faInfoCircle, faDownload);

const emit = defineEmits(['subs']);
const props = defineProps<{
  product: Product
}>();

const countSub = ref(1);

const rowsPrices = computed(() => {
  const rows: Record<any, unknown>[] = [];

  if (props.product.priceIsDouble) {
    rows.push({
      label: 'Arbeitspreis HT (brutto)',
      value: `${props.product.getPriceFormatted('workingPriceHt')}&thinsp;Cent/kWh`,
    });
    rows.push({
      label: 'Arbeitspreis NT (brutto)',
      value: `${props.product.getPriceFormatted('workingPriceNt')}&thinsp;Cent/kWh`,
    });
  } else if (props.product.attributes.details.customerType === 'business') {
    rows.push({
      label: 'Arbeitspreis (netto) ',
      value: `${props.product.getPriceFormatted('workingPrice')}&thinsp;Cent/kWh`,
    });
  } else {
    rows.push({
      label: 'Arbeitspreis (brutto) ',
      value: `${props.product.getPriceFormatted('workingPrice')}&thinsp;Cent/kWh`,
    });
  }

  if (props.product.attributes.details.customerType === 'business') {
    rows.push({
      label: 'Grundpreis (netto)',
      value: `${props.product.getPriceFormatted('basePrice')}&thinsp;€/Monat`,
    });
  } else {
    rows.push({
      label: 'Grundpreis (brutto)',
      value: `${props.product.getPriceFormatted('basePrice')}&thinsp;€/Monat`,
    });
  }

  props.product.bonuses.forEach((bonus) => {
    rows.push({
      label: bonus.label,
      value: `${bonus.valueGrossFormatted}&thinsp;€`,
      info: bonus.description,
      sub: countSub.value,
    });

    countSub.value += 1;
  });

  rows.push({
    label: 'Kosten im 1. Jahr',
    value: `${props.product.getPriceFormatted('yearly')}&thinsp;€`,
  });

  rows.push({
    label: 'Vertragslaufzeit',
    value: `${props.product.contractTermLabel}`,
  });

  rows.push({
    label: 'Kündigungsfrist',
    value: `${props.product.noticePeriodLabel}`,
  });

  return rows;
});

const rowsDownloads = computed(() => {
  const rows: Record<'label' | 'href' | 'key', string>[] = [];

  /*
  if (props.product.priceFileUrl) {
    rows.push({
      key: 'pricefile',
      label: 'Preisblatt',
      href: props.product.priceFileUrl,
    });
  }
   */

  const agbDocument = props.product.getDocumentByKey('agb');
  if (agbDocument) {
    rows.push({
      key: agbDocument.key,
      label: agbDocument.name || '',
      href: agbDocument.url || '#',
    });
  }

  const avbDocument = props.product.getDocumentByKey('avb');
  if (avbDocument) {
    rows.push({
      key: avbDocument.key,
      label: avbDocument.name || '',
      href: avbDocument.url || '#',
    });
  }

  const bgbDocument = props.product.getDocumentByKey('bgb');
  if (bgbDocument) {
    rows.push({
      key: bgbDocument.key,
      label: bgbDocument.name || '',
      href: bgbDocument.url || '#',
    });
  }

  return rows;
});

onMounted(() => {
  const subs: { sub: number, info: string }[] = [];

  rowsPrices.value.forEach((row) => {
    if (row.info && row.sub) {
      subs.push({ sub: row.sub as number, info: row.info as string });
    }
  });

  emit('subs', subs);
});

</script>

<style lang="scss">
@import "src/scss/component-helpers";

.product-price-details {
  .product-price-details__prices {
    width: 100%;

    td {
      @include font-weight-fix;
      font-size: .75rem;
      line-height: 2;
      text-align: left;
      color: #293033;

      @media(min-width: map-get($grid-breakpoints, 'lg')) {
        font-size: .875rem;
      }

      + td {
        white-space: nowrap;
        text-align: right;
        vertical-align: top;
      }

      &.spacer {
        &:before {
          content: ' ';
          display: inline-block;
        }
      }
    }

    a {
      text-decoration: none;
      color: inherit;

      i, svg {
        margin-right: .5em;
      }

      &:hover {
        color: $primary;
      }
    }
  }

  .product-price-details__downloads {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-top: 10px;

    a {
      flex: 0 0 auto;
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 10px;
      align-items: center;
      font-size: .75rem;
      text-decoration: none;
      color: $primary;

      @media(max-width: map-get($grid-breakpoints, 'md')) {
        flex: 0 0 fit-content !important;
        text-align: left;
      }

      @media(min-width: map-get($grid-breakpoints, 'lg')) {
        font-size: .875rem;
      }
    }
  }
}
</style>
